import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOrders } from '../hooks/useOrders';
import { useProducts } from '../hooks/useProducts';
import { useUsers } from '../hooks/useUsers';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart as OrderIcon,
  Inventory as ProductIcon,
  People as ClientIcon,
  Warehouse as WarehouseIcon,
  Receipt as InvoiceIcon,
  LocalShipping as ShipmentIcon,
  Analytics as ReportIcon,
  Settings as SettingIcon,
  TrendingUp as AnalyticsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol } from '../utils/invoice-pdf-general-section-utilities';

// Format currency with dynamic symbol
const formatCurrency = (amount, currency = 'EUR') => {
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const SearchResultsPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);


  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const { data: ordersData, isLoading: ordersLoading } = useOrders();
  const { data: productsData, isLoading: productsLoading } = useProducts();
  const { data: usersData, isLoading: usersLoading } = useUsers();

  const dataLoading = ordersLoading || productsLoading || usersLoading;

  useEffect(() => {
    if (!query) {
      setLoading(false);
      return;
    }
    
    // If data hooks are still loading, show loading state
    if (dataLoading) {
      setLoading(true);
      return;
    }
    
    // Data hooks finished loading, perform search
    performSearch(query);
  }, [query, dataLoading, ordersData, productsData, usersData]);

  const performSearch = async (searchQuery) => {
    setLoading(true);
    const searchResults = [];
    const searchLower = searchQuery.toLowerCase();

    try {
      // Search in orders
      try {
        const orders = ordersData?.items || ordersData || [];
        
        orders.forEach(order => {
          if (order.id?.toString().includes(searchQuery) ||
              order.status?.toLowerCase().includes(searchLower) ||
              order.customer_name?.toLowerCase().includes(searchLower) ||
              order.reference?.toLowerCase().includes(searchLower)) {
            searchResults.push({
              id: `order-${order.id}`,
              title: `Order #${order.id}`,
              subtitle: `Client: ${order.customer_name || '-'} - Status: ${order.status || '-'}`,
              path: '/client/orders',
              category: 'orders',
              icon: OrderIcon
            });
          }
        });
      } catch (error) { console.error('Error searching orders:', error); }

      // Search in products
      try {
        const products = productsData?.items || productsData || [];
        
        products.forEach(product => {
          if (product.name?.toLowerCase().includes(searchLower) ||
              product.sku?.toLowerCase().includes(searchLower) ||
              product.description?.toLowerCase().includes(searchLower) ||
              product.id?.toString().includes(searchQuery)) {
            searchResults.push({
              id: `product-${product.id}`,
              title: product.name || `Product #${product.id}`,
              subtitle: `SKU: ${product.sku || '-'} - Price: ${product.price ? formatCurrency(product.price, product.currency || 'EUR') : '-'}`,
              path: '/admin/products',
              category: 'products',
              icon: ProductIcon
            });
          }
        });
      } catch (error) { console.error('Error searching products:', error); }

      // Search in users
      try {
        const users = usersData?.items || usersData || [];
        
        users.forEach(user => {
          if (user.username?.toLowerCase().includes(searchLower) ||
              user.email?.toLowerCase().includes(searchLower) ||
              user.first_name?.toLowerCase().includes(searchLower) ||
              user.last_name?.toLowerCase().includes(searchLower)) {
            searchResults.push({
              id: `user-${user.id}`,
              title: user.username || `User #${user.id}`,
              subtitle: `${user.first_name || ''} ${user.last_name || ''} - ${user.email || '-'}`,
              path: '/admin/users',
              category: 'clients',
              icon: ClientIcon
            });
          }
        });
      } catch (error) { console.error('Error searching users:', error); }

      // Add system features search
      const features = [
        { id: 'FEAT-001', title: 'Analytics', subtitle: 'Dashboards and KPIs', path: '/client/analytics', category: 'features', icon: AnalyticsIcon },
        { id: 'FEAT-002', title: 'Marketplace Management', subtitle: 'Connected marketplaces management', path: '/client/marketplace', category: 'features', icon: SettingIcon },
        { id: 'FEAT-003', title: 'WMS Dashboard', subtitle: 'Warehouse management', path: '/client/wms-dashboard', category: 'features', icon: WarehouseIcon },
        { id: 'FEAT-004', title: 'TMS Dashboard', subtitle: 'Transport management', path: '/client/tms-dashboard', category: 'features', icon: ShipmentIcon },
        { id: 'FEAT-005', title: 'Support', subtitle: 'Help center and tickets', path: '/client/support', category: 'features', icon: ReportIcon },
        { id: 'FEAT-006', title: 'Orders', subtitle: 'Order management', path: '/client/orders', category: 'features', icon: OrderIcon },
        { id: 'FEAT-007', title: 'Products', subtitle: 'Product catalog', path: '/admin/products', category: 'features', icon: ProductIcon },
        { id: 'FEAT-008', title: 'Inventory', subtitle: 'Stock management', path: '/admin/inventory', category: 'features', icon: WarehouseIcon },
        { id: 'FEAT-009', title: 'Invoices', subtitle: 'Billing and invoices', path: '/client/invoices', category: 'features', icon: InvoiceIcon },
        { id: 'FEAT-010', title: 'Shipments', subtitle: 'Shipping and logistics', path: '/client/shipments', category: 'features', icon: ShipmentIcon },
      ];

      features.forEach(feature => {
        if (feature.title.toLowerCase().includes(searchLower) ||
            feature.subtitle.toLowerCase().includes(searchLower)) {
          searchResults.push(feature);
        }
      });

    } catch (error) { console.error('Error in search:', error); }

    setResults(searchResults);
    setLoading(false);
  };

  const handleResultClick = (result) => {
    navigate(result.path);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'orders': 'primary',
      'products': 'secondary',
      'clients': 'success',
      'warehouses': 'warning',
      'features': 'info'
    };
    return colors[category] || 'default';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'orders': 'Orders',
      'products': 'Products',
      'clients': 'Clients',
      'warehouses': 'Warehouses',
      'features': t('common.features', 'Features')
    };
    return labels[category] || category;
  };

  return (
    <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <SearchIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            {t('common.searchResults', 'Search Results')}
          </Typography>
          
          {query && (
            <Typography variant="h6" color="text.secondary">
              {t('common.searchFor', 'Search for')}: "<strong>{query}</strong>"
            </Typography>
          )}
        </Box>

        {/* Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ ml: 2 }}>
              {t('common.searchInProgress', 'Search in progress...')}
            </Typography>
          </Box>
        ) : !query ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('common.noSearchQuery', 'No search query provided. Use the search bar to get started.')}
          </Alert>
        ) : results.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('common.noResultsFound', 'No results found for "{{query}}". Try different keywords.', { query })}
          </Alert>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 3 }}>
              {t('common.resultsFound', '{{count}} result(s) found', { count: results.length })}
            </Typography>

            <Grid container spacing={3}>
              {results.map((result) => (
                <Grid item xs={12} md={6} lg={4} key={result.id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => handleResultClick(result)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <result.icon sx={{ fontSize: 40, color: 'primary.main', mt: 0.5 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            {result.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {result.subtitle}
                          </Typography>
                          <Chip 
                            label={getCategoryLabel(result.category)}
                            color={getCategoryColor(result.category)}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
  );
};

export default React.memo(SearchResultsPage);

